import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  BarChart3, TrendingUp, Users, Package, DollarSign,
  Clock, MapPin, Calendar, Award, Activity, Wallet, PiggyBank, Download, FileText
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const AdminReporting = () => {
  const { getAuthHeader } = useAuth();
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/reports`, getAuthHeader());
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportPDF = () => {
    if (!reports) return;
    
    setExporting(true);
    try {
      const doc = new jsPDF();
      const currentDate = new Date().toLocaleDateString();
      
      // Title
      doc.setFontSize(20);
      doc.setTextColor(102, 51, 153); // Purple
      doc.text('Profit and Loss Report', 20, 20);
      
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Generated: ${currentDate}`, 20, 28);
      
      // Financial Summary
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Financial Summary', 20, 45);
      
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      
      const financials = [
        ['Total Earnings (Client Payments)', `$${reports.overview.total_earnings.toLocaleString()}`],
        ['Total Paid Out (Worker Payments)', `$${reports.overview.total_paid_out.toLocaleString()}`],
        ['Gross Profit', `$${reports.overview.gross_profit.toLocaleString()}`],
      ];
      
      let yPos = 55;
      financials.forEach(([label, value]) => {
        doc.text(label + ':', 25, yPos);
        doc.setTextColor(0, 128, 0);
        doc.text(value, 120, yPos);
        doc.setTextColor(60, 60, 60);
        yPos += 8;
      });
      
      // This Year Breakdown
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('This Year Breakdown', 20, yPos + 10);
      
      yPos += 20;
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      
      const thisYear = [
        ['Client Payments (This Year)', `$${reports.yearly?.client_payments?.toLocaleString() || '0'}`],
        ['Worker Payments (This Year)', `$${reports.yearly?.paid_out?.toLocaleString() || '0'}`],
        ['Gross Profit (This Year)', `$${reports.yearly?.gross_profit?.toLocaleString() || '0'}`],
      ];
      
      thisYear.forEach(([label, value]) => {
        doc.text(label + ':', 25, yPos);
        doc.setTextColor(0, 100, 200);
        doc.text(value, 120, yPos);
        doc.setTextColor(60, 60, 60);
        yPos += 8;
      });
      
      // Operational Stats
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Operational Statistics', 20, yPos + 10);
      
      yPos += 20;
      doc.setFontSize(11);
      doc.setTextColor(60, 60, 60);
      
      const stats = [
        ['Total Deliveries', reports.overview.total_deliveries.toLocaleString()],
        ['Total Distance', `${reports.overview.total_distance_miles.toFixed(1)} miles`],
        ['Total Sessions', reports.overview.total_sessions.toLocaleString()],
        ['Active Workers', (reports.top_performers?.length || 0).toString()],
      ];
      
      stats.forEach(([label, value]) => {
        doc.text(label + ':', 25, yPos);
        doc.text(value, 120, yPos);
        yPos += 8;
      });
      
      // Monthly Data Table
      if (reports.monthly_graph?.current_year_data?.length > 0) {
        doc.setFontSize(14);
        doc.setTextColor(0, 0, 0);
        doc.text('Monthly Gross Profit Comparison', 20, yPos + 15);
        
        yPos += 25;
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('Month', 25, yPos);
        doc.text('Current Year', 70, yPos);
        doc.text('Previous Year', 110, yPos);
        doc.text('Change', 155, yPos);
        
        yPos += 5;
        doc.setDrawColor(200, 200, 200);
        doc.line(20, yPos, 190, yPos);
        
        yPos += 5;
        doc.setTextColor(60, 60, 60);
        
        reports.monthly_graph.current_year_data.forEach((data, idx) => {
          if (yPos > 270) {
            doc.addPage();
            yPos = 20;
          }
          
          const prevData = reports.monthly_graph.previous_year_data?.[idx] || {};
          const change = data.gross_profit - (prevData.gross_profit || 0);
          
          doc.text(MONTHS[idx], 25, yPos);
          doc.text(`$${data.gross_profit.toLocaleString()}`, 70, yPos);
          doc.text(`$${(prevData.gross_profit || 0).toLocaleString()}`, 110, yPos);
          
          if (change >= 0) {
            doc.setTextColor(0, 128, 0);
            doc.text(`+$${change.toLocaleString()}`, 155, yPos);
          } else {
            doc.setTextColor(200, 0, 0);
            doc.text(`-$${Math.abs(change).toLocaleString()}`, 155, yPos);
          }
          doc.setTextColor(60, 60, 60);
          
          yPos += 7;
        });
      }
      
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('Local Flyer Delivery - Confidential Financial Report', 20, 285);
      
      // Save the PDF
      doc.save(`profit_and_loss_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = () => {
    if (!reports) return;
    
    setExporting(true);
    try {
      let csvContent = 'Profit and Loss Report\n';
      csvContent += `Generated,${new Date().toLocaleDateString()}\n\n`;
      
      // Financial Summary
      csvContent += 'Financial Summary\n';
      csvContent += `Total Earnings (Client Payments),$${reports.overview.total_earnings}\n`;
      csvContent += `Total Paid Out (Worker Payments),$${reports.overview.total_paid_out}\n`;
      csvContent += `Gross Profit,$${reports.overview.gross_profit}\n\n`;
      
      // This Year
      csvContent += 'This Year Breakdown\n';
      csvContent += `Client Payments,$${reports.yearly?.client_payments || 0}\n`;
      csvContent += `Worker Payments,$${reports.yearly?.paid_out || 0}\n`;
      csvContent += `Gross Profit,$${reports.yearly?.gross_profit || 0}\n\n`;
      
      // Operational
      csvContent += 'Operational Statistics\n';
      csvContent += `Total Deliveries,${reports.overview.total_deliveries}\n`;
      csvContent += `Total Distance,${reports.overview.total_distance_miles.toFixed(1)} miles\n`;
      csvContent += `Total Sessions,${reports.overview.total_sessions}\n`;
      csvContent += `Active Workers,${reports.top_performers?.length || 0}\n\n`;
      
      // Monthly Data
      if (reports.monthly_graph?.current_year_data?.length > 0) {
        csvContent += 'Monthly Gross Profit\n';
        csvContent += 'Month,Current Year,Previous Year,Change\n';
        
        reports.monthly_graph.current_year_data.forEach((data, idx) => {
          const prevData = reports.monthly_graph.previous_year_data?.[idx] || {};
          const change = data.gross_profit - (prevData.gross_profit || 0);
          csvContent += `${MONTHS[idx]},$${data.gross_profit},$${prevData.gross_profit || 0},$${change}\n`;
        });
      }
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `profit_and_loss_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
    } catch (error) {
      console.error('Error exporting CSV:', error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-purple-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!reports) {
    return (
      <div className="p-6">
        <Card className="p-12 text-center border-0 shadow-sm">
          <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">Unable to load reports</p>
        </Card>
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending_approval': return 'bg-yellow-100 text-yellow-700';
      case 'approved': return 'bg-blue-100 text-blue-700';
      case 'paid': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // Prepare chart data
  const chartData = reports.monthly_graph?.current_year_data?.map((curr, idx) => ({
    name: MONTHS[idx],
    [`${reports.monthly_graph.current_year}`]: curr.gross_profit,
    [`${reports.monthly_graph.previous_year}`]: reports.monthly_graph.previous_year_data[idx]?.gross_profit || 0
  })) || [];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Profit and Loss</h1>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            disabled={exporting || !reports}
            className="flex items-center gap-2"
            data-testid="export-csv-btn"
          >
            <FileText size={16} />
            Export CSV
          </Button>
          <Button
            onClick={handleExportPDF}
            disabled={exporting || !reports}
            className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2"
            data-testid="export-pdf-btn"
          >
            {exporting ? (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <Download size={16} />
            )}
            Download PDF
          </Button>
          <button
            onClick={fetchReports}
            className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1 ml-2"
          >
            <Activity size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Financial Overview - Total Earnings, Paid Out, Gross Profit */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card className="p-6 border-0 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <DollarSign size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">${(reports.overview.total_earnings || 0).toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total Earnings (Client Payments)</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-0 shadow-sm bg-gradient-to-br from-orange-50 to-red-50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Wallet size={24} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">${(reports.overview.total_paid_out || 0).toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total Paid Out (Worker Pay)</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-0 shadow-sm bg-gradient-to-br from-green-50 to-emerald-50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <PiggyBank size={24} className="text-green-600" />
            </div>
            <div>
              <p className={`text-2xl font-bold ${(reports.overview.gross_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${(reports.overview.gross_profit || 0).toLocaleString()}
              </p>
              <p className="text-sm text-gray-500">Gross Profit</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Delivery Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card className="p-6 border-0 shadow-sm bg-gradient-to-br from-purple-50 to-indigo-50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
              <Package size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{reports.overview.total_deliveries.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total Deliveries</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-0 shadow-sm bg-gradient-to-br from-cyan-50 to-blue-50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-cyan-100 rounded-xl flex items-center justify-center">
              <MapPin size={24} className="text-cyan-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{reports.overview.total_distance_miles} mi</p>
              <p className="text-sm text-gray-500">Total Distance</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-0 shadow-sm bg-gradient-to-br from-amber-50 to-orange-50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
              <Clock size={24} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{reports.overview.total_sessions}</p>
              <p className="text-sm text-gray-500">Total Sessions</p>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-0 shadow-sm bg-gradient-to-br from-pink-50 to-rose-50">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
              <Users size={24} className="text-pink-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{reports.top_performers?.length || 0}</p>
              <p className="text-sm text-gray-500">Active Workers</p>
            </div>
          </div>
        </Card>
      </div>

      {/* This Year Section */}
      {reports.yearly && (
        <Card className="p-6 border-0 shadow-sm mb-8 bg-gradient-to-r from-violet-50 to-purple-50">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={20} className="text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">This Year ({new Date().getFullYear()})</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <p className="text-sm text-gray-500">Deliveries</p>
              <p className="text-2xl font-bold text-gray-900">{reports.yearly.deliveries?.toLocaleString() || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Client Payments</p>
              <p className="text-2xl font-bold text-blue-600">${reports.yearly.client_payments?.toLocaleString() || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Paid to Workers</p>
              <p className="text-2xl font-bold text-orange-600">${reports.yearly.paid_out?.toLocaleString() || 0}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Gross Profit</p>
              <p className={`text-2xl font-bold ${(reports.yearly.gross_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${reports.yearly.gross_profit?.toLocaleString() || 0}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Year-over-Year Profit Graph */}
      {chartData.length > 0 && (
        <Card className="p-6 border-0 shadow-sm mb-8">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={20} className="text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">Monthly Gross Profit Comparison</h2>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value) => `$${value}`} />
                <Tooltip formatter={(value) => [`$${value}`, '']} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey={`${reports.monthly_graph.current_year}`}
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name={`${reports.monthly_graph.current_year}`}
                />
                <Line
                  type="monotone"
                  dataKey={`${reports.monthly_graph.previous_year}`}
                  stroke="#94a3b8"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={{ r: 4 }}
                  name={`${reports.monthly_graph.previous_year}`}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Weekly & Monthly Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card className="p-6 border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <Calendar size={20} className="text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">This Week</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Deliveries</span>
              <span className="font-semibold">{reports.weekly.deliveries.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Worker Earnings</span>
              <span className="font-semibold text-green-600">${reports.weekly.earnings.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Sessions</span>
              <span className="font-semibold">{reports.weekly.sessions}</span>
            </div>
          </div>
        </Card>

        <Card className="p-6 border-0 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={20} className="text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">This Month</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Deliveries</span>
              <span className="font-semibold">{reports.monthly.deliveries.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Worker Earnings</span>
              <span className="font-semibold text-green-600">${reports.monthly.earnings.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Sessions</span>
              <span className="font-semibold">{reports.monthly.sessions}</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminReporting;
